import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { api } from "../../helpers/utils";

export default function CreatorsRedeemRequests() {
  const nav = useNavigate();
  const [redeemRequests, setRedeemRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  const getAllRedeemRequests = async () => {
    setLoadingRequests(true);
    try {
      const res = await api.post("https://api1.pepchat.in/admin/get-all-redeem-requests");
      // console.log("Redeem Requests Response:", res.data); 
      if (res.data.status === 100) {
        setRedeemRequests(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching redeem requests:", err);
    } finally {
      setLoadingRequests(false);
    }
  };

  const completeRedeemRequest = async (redeemId) => {
    setProcessingId(redeemId);
    try {
      const res = await api.post("https://api1.pepchat.in/admin/complete-redeem-request", {
        redeemId,
      });
      if (res.data.status === 100) {
        setRedeemRequests((prev) => prev.filter((req) => req.redeemId !== redeemId));
        alert("Redeem request completed successfully!");
      } else {
        alert("Failed to complete request. Please try again.");
      }
    } catch (err) {
      console.error("Error completing redeem request:", err);
      alert("Error processing request. Please try again.");
    } finally {
      setProcessingId(null);
    }
  };

  useEffect(() => {
    getAllRedeemRequests();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Wrapper>
      <HeaderRow>
        <BackButton onClick={() => nav("/admin/home")}>
          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back
        </BackButton>
        <HeaderArea>
          <MainTitle>Creators Redeem Requests</MainTitle>
          <SubTitle>Manage pending withdrawal requests from creators</SubTitle>
        </HeaderArea>
      </HeaderRow>

      {loadingRequests ? (
        <LoadingContainer>
          <Spinner />
          <LoadingText>Loading redeem requests...</LoadingText>
        </LoadingContainer>
      ) : redeemRequests.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="8" y="12" width="48" height="40" rx="4"></rect>
              <path d="M8 20h48"></path>
              <rect x="40" y="28" width="8" height="8" rx="2"></rect>
            </svg>
          </EmptyIcon>
          <EmptyText>No pending redeem requests</EmptyText>
          <EmptySubText>All requests have been processed</EmptySubText>
        </EmptyState>
      ) : (
        <RequestsContainer>
          {redeemRequests.map((request) => (
            <RequestCard key={request.redeemId}>
              <CreatorInfo>
                {request.creatorProfilePic ? (
                  <ProfilePic
                    src={request.creatorProfilePic}
                    alt={request.creatorName}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <DefaultAvatar style={{ display: request.creatorProfilePic ? 'none' : 'flex' }}>
                  <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="16" cy="8" r="5"></circle>
                    <path d="M3 21c0-5 5-8 13-8s13 3 13 8"></path>
                  </svg>
                </DefaultAvatar>
                <CreatorDetails>
                  <CreatorName>{request.creatorName}</CreatorName>
                  <CreatorMeta>
                    <MetaItem>
                      <MetaLabel>Mobile:</MetaLabel>
                      <MetaValue>{request.creatorMobile || "N/A"}</MetaValue>
                    </MetaItem>
                    <MetaItem>
                      <MetaLabel>Creator ID:</MetaLabel>
                      <MetaValue>{request.creatorId}</MetaValue>
                    </MetaItem>
                  </CreatorMeta>
                </CreatorDetails>
              </CreatorInfo>

              <Divider />

              <RequestDetails>
                <DetailRow>
                  <DetailLabel>Redeem Amount</DetailLabel>
                  <AmountBadge>₹{request.redeemAmount || 0}</AmountBadge>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Request ID</DetailLabel>
                  <DetailValue>{request.redeemId}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Timestamp</DetailLabel>
                  <DetailValue>{formatDate(request.timestamp)}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Status</DetailLabel>
                  <StatusBadge status={request.status}>
                    {request.status || "pending"}
                  </StatusBadge>
                </DetailRow>
              </RequestDetails>

              <ActionButton
                onClick={() => completeRedeemRequest(request.redeemId)}
                disabled={processingId === request.redeemId}
              >
                {processingId === request.redeemId ? (
                  <>
                    <ButtonSpinner />
                    Processing...
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                    Complete Request
                  </>
                )}
              </ActionButton>
            </RequestCard>
          ))}
        </RequestsContainer>
      )}
    </Wrapper>
  );
}

/* ---------------- STYLES ---------------- */

const Wrapper = styled.div`
  min-height: 100vh;
  background: #000000;
  padding: 40px 20px 80px;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
`;

const HeaderRow = styled.div`
  width: min(1000px, 100%);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #000000;
  border: 1px solid #ffffff;
  color: #ffffff;
  font-weight: 800;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;

  &:hover {
    background: #ffffff;
    color: #000000;
    transform: translateX(-4px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const HeaderArea = styled.div`
  text-align: center;
`;

const MainTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0;

  @media (max-width: 560px) {
    font-size: 28px;
  }
`;

const SubTitle = styled.div`
  margin-top: 8px;
  font-size: 15px;
  color: #bdbdbd;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 80px 20px;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #303030;
  border-top-color: #ffffff;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  font-size: 16px;
  color: #bdbdbd;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 80px 20px;
`;

const EmptyIcon = styled.div`
  opacity: 0.5;
  margin-bottom: 10px;
`;

const EmptyText = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
`;

const EmptySubText = styled.div`
  font-size: 15px;
  color: #8c8c8c;
`;

const RequestsContainer = styled.div`
  width: min(1000px, 100%);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RequestCard = styled.div`
  background: #050505;
  border: 1px solid #303030;
  padding: 24px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #ffffff;
  }

  @media (max-width: 640px) {
    padding: 20px;
  }
`;

const CreatorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
`;

const ProfilePic = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #303030;
  background: #050505;

  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
  }
`;

const DefaultAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 2px solid #303030;
  background: #000000;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 480px) {
    width: 50px;
    height: 50px;

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const CreatorDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CreatorName = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #ffffff;

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const CreatorMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (min-width: 640px) {
    flex-direction: row;
    gap: 20px;
  }
`;

const MetaItem = styled.div`
  display: flex;
  gap: 6px;
  font-size: 13px;
`;

const MetaLabel = styled.span`
  color: #8c8c8c;
`;

const MetaValue = styled.span`
  color: #ffffff;
  font-weight: 500;
`;

const Divider = styled.div`
  height: 1px;
  background: #303030;
  margin: 20px 0;
`;

const RequestDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DetailLabel = styled.div`
  font-size: 13px;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0;
`;

const DetailValue = styled.div`
  font-size: 14px;
  color: #ffffff;
  font-weight: 500;
  word-break: break-all;
`;

const AmountBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background: #000000;
  border: 1px solid #303030;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  width: fit-content;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: #050505;
  border: 1px solid #303030;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  text-transform: capitalize;
  width: fit-content;
`;

const ActionButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  background: #ffffff;
  border: 1px solid #ffffff;
  color: #000000;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #000000;
    color: #ffffff;
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ButtonSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
